import { useCallback, useState } from 'react';
import type { Node } from '@xyflow/react';
import type * as Api from '@ViewModels';
import type { BaseNodeData, AutomationStepNodeData } from '../types';

/**
 * Result from a node search operation.
 */
export interface NodeSearchResult {
	/** Unique ID of the matching node */
	nodeId: string;
	/** Step type of the node (if applicable) */
	stepType?: Api.AutomationStepType;
	/** Display title for the search result */
	title: string;
	/** Node type identifier (e.g., 'trigger', 'segment', step type) */
	nodeType: string;
}

/**
 * Current state of the node search panel.
 */
export interface NodeSearchState {
	/** Whether the search panel is currently open */
	isOpen: boolean;
	/** Current search query string */
	query: string;
	/** Filtered search results based on the query */
	results: NodeSearchResult[];
}

interface UseNodeSearchParams {
	/** Current nodes in the flow */
	nodes: Node<BaseNodeData>[];
	/** Function to update node state (for selecting nodes) */
	setNodes: React.Dispatch<React.SetStateAction<Node<BaseNodeData>[]>>;
	/** Function to center the viewport on a position */
	setCenter: (x: number, y: number, options?: { zoom?: number; duration?: number }) => void;
}

interface UseNodeSearchReturn {
	/** Current search state (isOpen, query, results) */
	searchState: NodeSearchState;
	/** Open the search panel */
	openSearch: () => void;
	/** Close the search panel and clear state */
	closeSearch: () => void;
	/** Update the search query and filter results */
	updateQuery: (query: string) => void;
	/** Navigate to a specific node (select and center view) */
	navigateToNode: (nodeId: string) => void;
}

/**
 * Hook for searching and navigating to nodes in the automation flow canvas.
 *
 * Provides search functionality that filters nodes by:
 * - Step type name (e.g., "email", "texting")
 * - Node title/name
 * - Node type (trigger, segment)
 *
 * @example
 * ```tsx
 * const { searchState, openSearch, closeSearch, updateQuery, navigateToNode } = useNodeSearch({
 *   nodes,
 *   setNodes,
 *   setCenter,
 * });
 *
 * // Open search with Ctrl+F
 * // Type "email" to filter for email nodes
 * // Click result or press Enter to navigate
 * ```
 */
export function useNodeSearch({ nodes, setNodes, setCenter }: UseNodeSearchParams): UseNodeSearchReturn {
	const [searchState, setSearchState] = useState<NodeSearchState>({
		isOpen: false,
		query: '',
		results: [],
	});

	/**
	 * Get display title for a node based on its type and data.
	 */
	const getNodeTitle = useCallback((node: Node<BaseNodeData>): string => {
		const data = node.data as AutomationStepNodeData;

		// Check for explicit title in data
		if (data.label) {
			return data.label;
		}

		// Check for step name
		if (data.stepData?.name) {
			return data.stepData.name;
		}

		// Check for automation step name
		if (data.automationStep?.name) {
			return data.automationStep.name;
		}

		// Fallback to step type name or node type
		if (data.stepType) {
			return formatStepType(data.stepType);
		}

		// Handle trigger nodes
		if (node.type === 'trigger') {
			return 'Trigger';
		}

		// Handle segment nodes
		if (node.type === 'segment') {
			return `Segment ${(data as { segmentIndex?: number }).segmentIndex ?? 0 + 1}`;
		}

		return node.type || 'Unknown';
	}, []);

	/**
	 * Get node type label for display.
	 */
	const getNodeTypeLabel = useCallback((node: Node<BaseNodeData>): string => {
		const data = node.data as AutomationStepNodeData;

		if (data.stepType) {
			return formatStepType(data.stepType);
		}

		if (node.type === 'trigger') {
			return 'Trigger';
		}

		if (node.type === 'segment') {
			return 'Segment';
		}

		return node.type || 'Node';
	}, []);

	/**
	 * Search nodes based on the query string.
	 */
	const searchNodes = useCallback(
		(query: string): NodeSearchResult[] => {
			if (!query.trim()) {
				return [];
			}

			const lowerQuery = query.toLowerCase().trim();

			return nodes
				.filter(node => {
					// Skip phantom/utility nodes
					if (node.type === 'phantomTarget') {
						return false;
					}

					const title = getNodeTitle(node).toLowerCase();
					const typeLabel = getNodeTypeLabel(node).toLowerCase();

					// Match against title, step type, or node type
					return (
						title.includes(lowerQuery) ||
						typeLabel.includes(lowerQuery) ||
						node.type?.toLowerCase().includes(lowerQuery)
					);
				})
				.map(node => ({
					nodeId: node.id,
					stepType: (node.data as AutomationStepNodeData).stepType,
					title: getNodeTitle(node),
					nodeType: getNodeTypeLabel(node),
				}))
				.slice(0, 20); // Limit results to 20
		},
		[nodes, getNodeTitle, getNodeTypeLabel]
	);

	const openSearch = useCallback(() => {
		setSearchState(prev => ({
			...prev,
			isOpen: true,
			query: '',
			results: [],
		}));
	}, []);

	const closeSearch = useCallback(() => {
		setSearchState({
			isOpen: false,
			query: '',
			results: [],
		});
	}, []);

	const updateQuery = useCallback(
		(query: string) => {
			const results = searchNodes(query);
			setSearchState(prev => ({
				...prev,
				query,
				results,
			}));
		},
		[searchNodes]
	);

	const navigateToNode = useCallback(
		(nodeId: string) => {
			const node = nodes.find(n => n.id === nodeId);
			if (!node) {
				return;
			}

			// Select the node (deselect all others)
			setNodes(nds =>
				nds.map(n => ({
					...n,
					selected: n.id === nodeId,
				}))
			);

			// Center view on the node with some offset for the node dimensions
			const nodeWidth = node.measured?.width || 300;
			const nodeHeight = node.measured?.height || 100;
			setCenter(node.position.x + nodeWidth / 2, node.position.y + nodeHeight / 2, { zoom: 1, duration: 400 });

			// Close the search panel
			closeSearch();
		},
		[nodes, setNodes, setCenter, closeSearch]
	);

	return {
		searchState,
		openSearch,
		closeSearch,
		updateQuery,
		navigateToNode,
	};
}

/**
 * Format a step type enum value for display.
 */
function formatStepType(stepType: Api.AutomationStepType): string {
	// Convert enum to readable string
	const typeString = String(stepType);
	// Add spaces before capitals and capitalize first letter
	return typeString
		.replace(/([A-Z])/g, ' $1')
		.trim()
		.replace(/^./, str => str.toUpperCase());
}
